/* ============================================================
   <quantity-bundle>
   Troca o produto real adicionado ao carrinho conforme a opção
   de quantidade selecionada. Atualiza o input [name=id] do
   product-form e sincroniza o bloco de preço principal.
   ============================================================ */
if (!customElements.get('quantity-bundle')) {
  customElements.define(
    'quantity-bundle',
    class QuantityBundle extends HTMLElement {
      connectedCallback() {
        this.sectionEl = this.closest('.shopify-section') || document;
        this.priceTarget = document.querySelector(
          this.getAttribute('data-price-target') || ''
        );
        this.inputs = Array.from(
          this.querySelectorAll('.qb-card__input')
        );

        this.inputs.forEach((input) => {
          input.addEventListener('change', () => this.onSelect(input));
        });

        // Sincroniza estado inicial com a opção marcada (garante que
        // form e preço batem com o card selecionado mesmo quando o
        // produto da PDP não está na lista do metacampo).
        const checked = this.inputs.find((i) => i.checked && !i.disabled);
        if (checked) this.onSelect(checked, true);

        // Pré-carrega as fotos dos kits com a página ociosa: sem isso, a 1ª troca
        // de cada kit espera o download e a foto anterior fica na tela.
        const schedulePreload = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => this.preloadKitImages(), { timeout: 3000 });
          } else {
            window.setTimeout(() => this.preloadKitImages(), 1500);
          }
        };
        if (document.readyState === 'complete') schedulePreload();
        else window.addEventListener('load', schedulePreload, { once: true });
      }

      onSelect(input, isInitial) {
        if (input.disabled) return;

        const variantId = input.getAttribute('data-qb-variant-id');
        if (!variantId) return;

        // Estado visual
        this.querySelectorAll('.qb-card').forEach((card) =>
          card.classList.remove('is-selected')
        );
        const card = input.closest('.qb-card');
        if (card) card.classList.add('is-selected');

        this.updateFormVariant(variantId);
        this.updateButtonLabels(
          input.getAttribute('data-qb-qty-label'),
          input.getAttribute('data-qb-price-cents')
        );
        this.updatePriceExtras(input);
        this.updateMainImage(input, isInitial);

        // No load inicial não force re-render de preço se já bate,
        // mas sincroniza para garantir consistência visual.
        if (card) this.syncPrice(card);
      }

      updateButtonLabels(label, priceCents) {
        const installment = this.buildInstallmentText(priceCents);
        this.updateAddButtonLabel(label);
        this.updateBuyNowButtonLabel(label, installment);
      }

      buildInstallmentText(priceCents) {
        if (this.dataset.atcInstallment !== 'true') return '';
        const cents = Number(priceCents);
        if (!Number.isFinite(cents) || cents <= 0) return '';
        const max = Number(this.dataset.installmentsMax || 6);
        if (max < 1) return '';
        const per = Math.floor(cents / max);
        const suffix = this.dataset.installmentsSuffix || 'sem juros';
        return max + 'x de ' + this.formatMoney(per, this) + ' ' + suffix;
      }

      updateAddButtonLabel(label) {
        const btn = this.sectionEl.querySelector('.product-form__submit');
        if (!btn) return;
        const span = btn.querySelector('span');
        if (!span) return;
        if (this.baseAddText == null) {
          this.baseAddText =
            (window.variantStrings && window.variantStrings.addToCart) ||
            span.textContent.trim();
        }
        span.textContent = label
          ? this.baseAddText + ' (' + label + ')'
          : this.baseAddText;
      }

      updateBuyNowButtonLabel(label, installment, attempt = 0) {
        const buttons = this.sectionEl.querySelectorAll(
          '.shopify-payment-button__button--unbranded'
        );
        if (!buttons.length) {
          if (attempt < 10) {
            window.setTimeout(
              () => this.updateBuyNowButtonLabel(label, installment, attempt + 1),
              100
            );
          }
          return;
        }

        const main = label ? 'Comprar agora (' + label + ')' : 'Comprar agora';

        buttons.forEach((btn) => {
          // SEGURANÇA: só usamos as MESMAS operações que as outras PDPs já fazem
          // no botão nativo da Shopify — textContent + aria-label. NUNCA injetar
          // <span>/filhos nem setar atributos observados (data-*) no
          // shopify-buy-it-now-button: isso quebra o "prewarm" do checkout
          // acelerado e causa o delay grande até o redirect.
          // A 2ª linha (parcelamento) é apenas uma quebra de linha no MESMO
          // texto do botão, estilizada por CSS (.bm-buynow-2line ::first-line).
          if (installment) {
            btn.textContent = main + '\n' + installment;
            btn.setAttribute('aria-label', main + ' — ' + installment);
          } else {
            btn.textContent = main;
            btn.setAttribute('aria-label', main);
          }
        });
      }

      updateFormVariant(variantId) {
        const idInputs = this.sectionEl.querySelectorAll(
          'product-form [name="id"], .product-variant-id'
        );
        idInputs.forEach((el) => {
          el.value = variantId;
          el.removeAttribute('disabled');
        });
      }

      updatePriceExtras(input) {
        const priceCents = Number(input.getAttribute('data-qb-price-cents'));
        if (!Number.isFinite(priceCents) || priceCents <= 0) return;

        const sectionId = this.getAttribute('data-section-id');
        const extras =
          (sectionId && document.getElementById('VhPriceExtras-' + sectionId)) ||
          this.sectionEl.querySelector('[data-vh-price-extras]');
        if (!extras) return;

        const maxInstallments = Number(extras.dataset.maxInstallments || 0);
        const pixPct = Number(extras.dataset.pixPct || 0);

        if (maxInstallments > 1) {
          const installmentCents = Math.floor(priceCents / maxInstallments);
          const installmentLine = extras.querySelector('.vh-installments');
          if (installmentLine) {
            const prefix = extras.dataset.installmentsPrefix || 'ou';
            const suffix = extras.dataset.installmentsSuffix || 'sem juros';
            const strong = document.createElement('strong');
            strong.textContent =
              maxInstallments + 'x de ' + this.formatMoney(installmentCents, extras);

            installmentLine.textContent = '';
            installmentLine.append(
              document.createTextNode(prefix + ' '),
              strong,
              document.createTextNode(' ' + suffix)
            );
          }
        }

        if (pixPct > 0) {
          const pixFinalCents = priceCents - Math.floor((priceCents * pixPct) / 100);
          const pixAmount = extras.querySelector('.vh-pix-line__amount');
          if (pixAmount) pixAmount.textContent = this.formatMoney(pixFinalCents, extras);
        }
      }

      formatMoney(cents, context) {
        const currency = context.dataset.currency || 'BRL';
        const locale =
          context.dataset.locale ||
          document.documentElement.lang ||
          'pt-BR';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          })
            .format(cents / 100)
            .replace(/\u00a0/g, ' ');
        } catch (error) {
          return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
        }
      }

      syncPrice(card) {
        if (!this.priceTarget) return;
        const tpl = card.querySelector('template[data-qb-price-sync]');
        if (!tpl) return;
        this.priceTarget.innerHTML = tpl.innerHTML;
      }

      // A foto do kit entra sempre no 1º slide da galeria (e na miniatura dele).
      // Trocar o slide ativo substituiria uma arte da PDP (ex.: "Benefícios").
      getHero() {
        const sectionId = this.getAttribute('data-section-id');
        const gallery = document.getElementById('MediaGallery-' + sectionId);
        if (!gallery) return null;

        const slide = gallery.querySelector('.product__media-list .product__media-item');
        const img = slide && slide.querySelector('.product__media img');
        if (!img) return null;

        const thumbItem = gallery.querySelector(
          '.thumbnail-list [data-target="' + slide.dataset.mediaId + '"]'
        );
        const thumb = thumbItem && thumbItem.querySelector('img');

        // Mesma foto no lightbox (zoom): o modal acha a imagem pelo id da mídia do botão do slide.
        const toggle = slide.querySelector('.product__media-toggle[data-media-id]');
        const modalImg = toggle
          ? document.querySelector(
              '#ProductModal-' + sectionId + ' img[data-media-id="' + toggle.dataset.mediaId + '"]'
            )
          : null;

        if (!this.heroOriginal) {
          this.heroOriginal = {
            img: this.readImage(img),
            thumb: thumb ? this.readImage(thumb) : null,
            modal: modalImg ? this.readImage(modalImg) : null,
          };
        }

        return { gallery, slide, img, thumbItem, thumb, modalImg };
      }

      readImage(img) {
        return {
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          alt: img.getAttribute('alt'),
        };
      }

      // srcset antes do src: o navegador escolhe o candidato uma vez só.
      writeImage(img, data, swapped) {
        if (data.srcset) img.setAttribute('srcset', data.srcset);
        else img.removeAttribute('srcset');
        if (data.src) img.setAttribute('src', data.src);
        if (data.alt != null) img.setAttribute('alt', data.alt);
        // Kits têm proporção diferente da foto original: contain para não cortar.
        img.classList.toggle('qb-swapped', swapped);
      }

      updateMainImage(input, isInitial) {
        // PDP com fotos próprias (custom.featured_image_card_only): abre na 1ª foto
        // da galeria; a foto do kit só entra quando o cliente escolhe a quantidade.
        if (isInitial && this.dataset.keepHeroOnLoad === 'true') return;

        const isCurrentProduct = input.hasAttribute('data-qb-current-product');
        const imageSrc = input.getAttribute('data-qb-image-src');
        if (!isCurrentProduct && !imageSrc) return;

        const hero = this.getHero();
        if (!hero) return;

        if (isCurrentProduct && !imageSrc) {
          // Sem foto informada, a opção do próprio produto volta para a foto original do slide.
          this.writeImage(hero.img, this.heroOriginal.img, false);
          if (hero.thumb && this.heroOriginal.thumb) {
            this.writeImage(hero.thumb, this.heroOriginal.thumb, false);
          }
          if (hero.modalImg && this.heroOriginal.modal) {
            this.writeImage(hero.modalImg, this.heroOriginal.modal, false);
          }
        } else {
          // A foto do próprio produto (1 frasco) tem a proporção do slide: só os kits usam contain.
          const swapped = !isCurrentProduct;
          const alt = input.getAttribute('data-qb-image-alt');
          this.writeImage(
            hero.img,
            { src: imageSrc, srcset: input.getAttribute('data-qb-image-srcset'), alt },
            swapped
          );
          if (hero.modalImg) {
            // No modal a proporção vem da própria foto (height: auto): sem contain.
            this.writeImage(
              hero.modalImg,
              { src: imageSrc, srcset: input.getAttribute('data-qb-image-srcset'), alt },
              false
            );
          }
          const thumbSrc = input.getAttribute('data-qb-image-thumb');
          if (hero.thumb && thumbSrc) {
            this.writeImage(
              hero.thumb,
              { src: thumbSrc, srcset: input.getAttribute('data-qb-image-thumb-srcset'), alt },
              swapped
            );
          }
        }

        if (!isInitial) this.showHero(hero);
      }

      // Mostra o 1º slide sem rolar a página: o setActiveMedia nativo rola até a
      // galeria, o que no mobile puxaria a tela para cima ao tocar no kit.
      showHero({ gallery, slide, thumbItem }) {
        if (!slide.classList.contains('is-active')) {
          gallery
            .querySelectorAll('.product__media-item.is-active')
            .forEach((el) => el.classList.remove('is-active'));
          slide.classList.add('is-active');
        }

        const list = slide.parentElement;
        if (list && list.scrollLeft > 0) list.scrollTo({ left: 0 });

        if (thumbItem && typeof gallery.setActiveThumbnail === 'function') {
          gallery.setActiveThumbnail(thumbItem);
        }
        // Só existe vinculado ao componente quando há miniaturas.
        if (gallery.elements && gallery.elements.thumbnails) {
          window.requestAnimationFrame(() => gallery.syncThumbnailRailHeight());
        }
      }

      // Mesmo sizes do slide principal: o navegador baixa o mesmo candidato que
      // vai usar na troca, e a troca sai do cache.
      preloadKitImages() {
        const hero = this.getHero();
        const sizes = hero ? hero.img.getAttribute('sizes') : '';

        this.inputs.forEach((input) => {
          if (input.disabled) return;
          const src = input.getAttribute('data-qb-image-src');
          if (!src) return;

          const image = new Image();
          if (sizes) image.sizes = sizes;
          const srcset = input.getAttribute('data-qb-image-srcset');
          if (srcset) image.srcset = srcset;
          image.src = src;

          const thumbSrc = input.getAttribute('data-qb-image-thumb');
          if (hero && hero.thumb && thumbSrc) {
            const thumb = new Image();
            const thumbSrcset = input.getAttribute('data-qb-image-thumb-srcset');
            if (thumbSrcset) thumb.srcset = thumbSrcset;
            thumb.src = thumbSrc;
          }
        });
      }
    }
  );
}
